import { useRef, useState } from 'react';
import { motion, useMotionValue, useTransform, useScroll } from 'framer-motion';
import { PORTFOLIO } from '@/constants/testIds';

// One interactive keyword — hover reveals a tiny annotation chip below the word
const Word = ({ children, note, testId, tone = 'accent' }) => {
    const [hover, setHover] = useState(false);
    return (
        <span
            className="relative inline-block align-baseline"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            <span
                data-testid={testId}
                data-cursor="hover"
                className={`font-display-italic transition-colors duration-300 ${
                    tone === 'accent'
                        ? 'text-[var(--accent)]'
                        : 'text-[var(--ink)]'
                }`}
            >
                {children}
            </span>
            {/* animated underline stroke */}
            <motion.span
                aria-hidden
                initial={false}
                animate={{ scaleX: hover ? 1 : 0 }}
                transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
                style={{ transformOrigin: 'left' }}
                className={`pointer-events-none absolute left-0 right-0 -bottom-2 h-[3px] rounded-full ${
                    tone === 'accent' ? 'bg-[var(--accent)]' : 'bg-[var(--ink)]'
                }`}
            />
            {/* annotation chip */}
            <motion.span
                aria-hidden
                initial={false}
                animate={{
                    opacity: hover ? 1 : 0,
                    y: hover ? 0 : 8,
                    scale: hover ? 1 : 0.95,
                }}
                transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
                className="pointer-events-none absolute left-0 top-full mt-4 md:mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[var(--line-2)] bg-[var(--surface-2)] px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-[var(--ink-2)] font-mono-brand shadow-lg shadow-black/20 z-10"
            >
                <span className="w-1 h-1 rounded-full bg-[var(--accent)]" />
                {note}
            </motion.span>
        </span>
    );
};

// Meditating monk avatar — placed as an intentional left column companion,
// framed by a hand-drawn enso brush ring so the character feels like an
// anchor for the koan rather than a floating decoration. Same character
// identity as the Hero / About PortraitCards.
//
// Easter egg: on hover (or tap on touch), he opens one eye and winks.
// Two variants are pre-loaded and cross-faded so the swap is instant.
const MeditatingMonk = () => {
    const [winking, setWinking] = useState(false);
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-15%' }}
            transition={{ duration: 1.1, ease: [0.2, 0.8, 0.2, 1] }}
            data-testid="philosophy-monk-avatar"
            className="relative flex items-end justify-center h-full min-h-[220px] md:min-h-[420px] group"
            onHoverStart={() => setWinking(true)}
            onHoverEnd={() => setWinking(false)}
            onTapStart={() => setWinking(true)}
            onTap={() => setTimeout(() => setWinking(false), 1400)}
        >
            {/* Hand-drawn enso ring — a Zen brush circle that visually holds
                 the character. Rotates very slowly to feel alive. */}
            <motion.svg
                aria-hidden
                viewBox="0 0 100 100"
                className="absolute inset-0 w-full h-full pointer-events-none"
                animate={{ rotate: 360 }}
                transition={{ duration: 120, ease: 'linear', repeat: Infinity }}
            >
                <defs>
                    <linearGradient id="enso-stroke" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.9" />
                        <stop offset="55%" stopColor="var(--accent)" stopOpacity="0.5" />
                        <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
                    </linearGradient>
                </defs>
                {/* Broken/open enso — a signature Zen motif */}
                <path
                    d="M 50 8
                       A 42 42 0 1 1 22 88"
                    fill="none"
                    stroke="url(#enso-stroke)"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                />
            </motion.svg>

            {/* Warm accent halo behind the monk */}
            <div
                aria-hidden
                className="absolute inset-[10%] -z-0 blur-3xl opacity-50"
                style={{
                    background:
                        'radial-gradient(55% 55% at 50% 55%, color-mix(in oklab, var(--accent) 40%, transparent), transparent 70%)',
                }}
            />

            {/* Stacked monk sprites — the meditating base and the winking
                 hover state, cross-faded via opacity. Both share the same
                 slow floating-bob animation so the swap looks like a single
                 continuous character. */}
            <motion.div
                animate={{ y: [0, -6, 0, -3, 0] }}
                transition={{ duration: 6.5, repeat: Infinity, ease: 'easeInOut' }}
                className="relative z-[1] w-[70%] max-w-[280px] aspect-square"
                style={{ willChange: 'transform' }}
            >
                <img
                    src="/avatars/monk-meditation.webp"
                    srcSet="/avatars/monk-meditation.webp 1x, /avatars/monk-meditation@2x.webp 2x"
                    alt="Himanshu meditating"
                    decoding="async"
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-contain select-none drop-shadow-[0_18px_36px_rgba(0,0,0,0.4)] transition-opacity duration-300"
                    style={{ opacity: winking ? 0 : 1 }}
                />
                <img
                    src="/avatars/monk-wink.webp"
                    srcSet="/avatars/monk-wink.webp 1x, /avatars/monk-wink@2x.webp 2x"
                    alt=""
                    aria-hidden
                    decoding="async"
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-contain select-none drop-shadow-[0_18px_36px_rgba(0,0,0,0.4)] transition-opacity duration-300"
                    style={{ opacity: winking ? 1 : 0 }}
                />
            </motion.div>

            {/* Meditation base — a subtle floor shadow anchoring the character
                 so he doesn't feel like he's levitating awkwardly */}
            <div
                aria-hidden
                className="absolute left-1/2 -translate-x-1/2 bottom-[10%] w-[46%] h-2 rounded-full blur-md opacity-40"
                style={{ background: 'var(--ink)' }}
            />

            {/* Playful hover hint chip — appears briefly under the monk to
                 tell first-timers the character is interactive. Sits inside
                 the same container so it moves with the enso frame. */}
            <div
                aria-hidden
                className="absolute left-1/2 -translate-x-1/2 bottom-3 md:bottom-4 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            >
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--surface-2)]/85 backdrop-blur border border-[var(--line-2)] font-mono-brand text-[9px] uppercase tracking-[0.22em] text-[var(--ink-2)] whitespace-nowrap">
                    <span className="w-1 h-1 rounded-full bg-[var(--accent)]" />
                    Namaste · I see you
                </span>
            </div>
        </motion.div>
    );
};




const Philosophy = () => {
    const cardRef = useRef(null);
    const sectionRef = useRef(null);

    // Mouse-tracking spotlight glow
    const mx = useMotionValue(-500);
    const my = useMotionValue(-500);
    const onMove = (e) => {
        const rect = cardRef.current?.getBoundingClientRect();
        if (!rect) return;
        mx.set(e.clientX - rect.left);
        my.set(e.clientY - rect.top);
    };
    const onLeave = () => {
        mx.set(-500);
        my.set(-500);
    };
    const bg = useTransform(
        [mx, my],
        ([x, y]) =>
            `radial-gradient(500px circle at ${x}px ${y}px, color-mix(in oklab, var(--accent) 14%, transparent), transparent 60%)`
    );

    // Parallax quote glyphs
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start end', 'end start'],
    });
    const yOpen = useTransform(scrollYProgress, [0, 1], [-40, 40]);
    const yClose = useTransform(scrollYProgress, [0, 1], [40, -40]);

    return (
        <section
            id="philosophy"
            ref={sectionRef}
            data-testid={PORTFOLIO.philosophySection}
            className="relative px-4 md:px-6 py-10 md:py-14 overflow-hidden"
        >
            <div className="mx-auto max-w-[1400px]">
                <div
                    ref={cardRef}
                    onMouseMove={onMove}
                    onMouseLeave={onLeave}
                    className="relative rounded-[28px] border border-[var(--line)] bg-[var(--surface)] px-6 md:px-16 py-12 md:py-16 overflow-hidden"
                >
                    {/* dotted grid texture */}
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-0 opacity-[0.35] mix-blend-normal"
                        style={{
                            backgroundImage:
                                'radial-gradient(circle, color-mix(in oklab, var(--ink-3) 45%, transparent) 1px, transparent 1px)',
                            backgroundSize: '22px 22px',
                        }}
                    />

                    {/* ── Abstract manifesto backdrop ─────────────────────
                         Themeable SVG artefacts — concentric orbits at
                         top-right, flowing Bezier ribbons at bottom-left,
                         and a hairline diagonal mesh — all painted with
                         theme variables so both dark and light modes read
                         correctly. Kept at very low opacity so the
                         Manifesto typography stays the hero. */}
                    <svg
                        aria-hidden
                        viewBox="0 0 800 800"
                        preserveAspectRatio="xMaxYMin slice"
                        className="pointer-events-none absolute -top-16 -right-24 w-[70%] md:w-[55%] h-auto"
                    >
                        {[280, 220, 160, 100, 50].map((r, i) => (
                            <circle
                                key={r}
                                cx="600"
                                cy="220"
                                r={r}
                                fill="none"
                                stroke="var(--ink)"
                                strokeWidth={i === 0 ? 1.2 : 0.6}
                                opacity={0.10 - i * 0.014}
                                strokeDasharray={i % 2 === 0 ? '2 6' : undefined}
                            />
                        ))}
                        {/* Small accent orbit dot */}
                        <circle
                            cx="820"
                            cy="220"
                            r="4"
                            fill="var(--accent)"
                            opacity="0.45"
                        />
                    </svg>

                    <svg
                        aria-hidden
                        viewBox="0 0 800 600"
                        preserveAspectRatio="xMinYMax slice"
                        className="pointer-events-none absolute -bottom-8 -left-16 w-[80%] md:w-[60%] h-auto"
                    >
                        <defs>
                            <linearGradient id="ribbon-a" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.55" />
                                <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
                            </linearGradient>
                            <linearGradient id="ribbon-b" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor="var(--ink)" stopOpacity="0.35" />
                                <stop offset="100%" stopColor="var(--ink)" stopOpacity="0" />
                            </linearGradient>
                        </defs>
                        {/* Three flowing ribbons */}
                        <path
                            d="M-40 480 C 150 320, 350 560, 560 380 S 820 260, 900 320"
                            fill="none"
                            stroke="url(#ribbon-a)"
                            strokeWidth="1.5"
                            opacity="0.35"
                        />
                        <path
                            d="M-40 540 C 180 400, 380 620, 600 440 S 820 320, 900 380"
                            fill="none"
                            stroke="url(#ribbon-b)"
                            strokeWidth="1"
                            opacity="0.30"
                        />
                        <path
                            d="M-40 420 C 120 260, 320 500, 520 320 S 780 200, 900 260"
                            fill="none"
                            stroke="var(--ink)"
                            strokeWidth="0.6"
                            opacity="0.10"
                            strokeDasharray="2 8"
                        />
                    </svg>

                    {/* Fine diagonal mesh */}
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-0 opacity-[0.05]"
                        style={{
                            backgroundImage:
                                'repeating-linear-gradient(135deg, var(--ink) 0 1px, transparent 1px 42px)',
                        }}
                    />

                    {/* mouse spotlight */}
                    <motion.div
                        aria-hidden
                        style={{ background: bg }}
                        className="pointer-events-none absolute inset-0"
                    />
                    {/* corner glyphs — subtle parallax */}
                    <motion.div
                        aria-hidden
                        style={{ y: yOpen }}
                        className="pointer-events-none absolute -top-16 -left-8 font-display-italic text-[38vw] md:text-[22vw] leading-[0.75] text-[var(--ink)] opacity-[0.04] select-none"
                    >
                        &ldquo;
                    </motion.div>
                    <motion.div
                        aria-hidden
                        style={{ y: yClose }}
                        className="pointer-events-none absolute -bottom-24 -right-4 font-display-italic text-[38vw] md:text-[22vw] leading-[0.75] text-[var(--ink)] opacity-[0.04] select-none"
                    >
                        &rdquo;
                    </motion.div>

                    {/* Top row — label */}
                    <div className="relative flex items-center justify-between gap-4 flex-wrap mb-8 md:mb-12">
                        <div
                            data-testid="philosophy-manifesto-label"
                            className="inline-flex items-center gap-3 font-mono-brand text-[10.5px] uppercase tracking-[0.28em] text-[var(--ink-2)]"
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] pulse-dot shrink-0" />
                            <span className="w-8 h-[1px] bg-[var(--line-2)]" />
                            <span>Manifesto</span>
                        </div>
                        <div className="hidden sm:inline-flex items-center gap-2 font-mono-brand text-[10px] uppercase tracking-[0.26em] text-[var(--ink-3)]">
                            <span>A design koan</span>
                            <span className="w-1 h-1 rounded-full bg-[var(--accent)]" />
                            <span className="font-display-italic normal-case tracking-normal text-[var(--ink-2)]">
                                वि·कल्प
                            </span>
                        </div>
                    </div>

                    {/* Two-column body: meditating monk anchors the left,
                         the kinetic quote flows on the right. On mobile the
                         columns stack — monk on top, koan below. */}
                    <div className="relative grid grid-cols-12 gap-6 md:gap-10 items-center">
                        {/* LEFT — monk column */}
                        <div className="col-span-12 md:col-span-4 order-1">
                            <MeditatingMonk />
                        </div>

                        {/* RIGHT — quote column */}
                        <div className="col-span-12 md:col-span-8 order-2">
                            {/* Kinetic quote — words reveal with staggered fade */}
                            <motion.blockquote
                                data-testid={PORTFOLIO.philosophyQuote}
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true, margin: '-15%' }}
                                variants={{
                                    hidden: {},
                                    show: { transition: { staggerChildren: 0.08 } },
                                }}
                                className="relative font-display leading-[0.98] text-[9vw] md:text-[5.2vw] text-[var(--ink)]"
                            >
                        {[
                            'If',
                            'you',
                            'find',
                            'an',
                            'element',
                            'of',
                            'your',
                            'interface',
                            'requires',
                        ].map((w) => (
                            <motion.span
                                key={w}
                                variants={{
                                    hidden: { opacity: 0, y: 24 },
                                    show: {
                                        opacity: 1,
                                        y: 0,
                                        transition: { duration: 0.55, ease: [0.2, 0.8, 0.2, 1] },
                                    },
                                }}
                                className="inline-block mr-[0.22em]"
                            >
                                {w}
                            </motion.span>
                        ))}
                        <motion.span
                            variants={{
                                hidden: { opacity: 0, y: 24 },
                                show: {
                                    opacity: 1,
                                    y: 0,
                                    transition: { duration: 0.7, delay: 0.05, ease: [0.2, 0.8, 0.2, 1] },
                                },
                            }}
                            className="inline-block mr-[0.22em]"
                        >
                            <Word
                                note="= Friction"
                                testId="philosophy-word-instructions"
                                tone="accent"
                            >
                                instructions
                            </Word>
                        </motion.span>
                        <motion.span
                            variants={{
                                hidden: { opacity: 0 },
                                show: { opacity: 1, transition: { duration: 0.5, delay: 0.4 } },
                            }}
                            className="text-[var(--accent)]"
                        >
                            …
                        </motion.span>
                        <br />
                        <motion.span
                            variants={{
                                hidden: { opacity: 0, y: 24 },
                                show: {
                                    opacity: 1,
                                    y: 0,
                                    transition: { duration: 0.7, delay: 0.5, ease: [0.2, 0.8, 0.2, 1] },
                                },
                            }}
                            className="inline-block mr-[0.22em]"
                        >
                            <Word
                                note="= Clarity"
                                testId="philosophy-word-redesign"
                                tone="ink"
                            >
                                Redesign
                            </Word>
                        </motion.span>
                        {['it'].map((w) => (
                            <motion.span
                                key={w}
                                variants={{
                                    hidden: { opacity: 0, y: 24 },
                                    show: {
                                        opacity: 1,
                                        y: 0,
                                        transition: { duration: 0.55, delay: 0.6, ease: [0.2, 0.8, 0.2, 1] },
                                    },
                                }}
                                className="inline-block mr-[0.02em]"
                            >
                                {w}
                            </motion.span>
                        ))}
                        <motion.span
                            variants={{
                                hidden: { opacity: 0, scale: 0.6 },
                                show: {
                                    opacity: 1,
                                    scale: 1,
                                    transition: { duration: 0.55, delay: 0.75, ease: [0.2, 0.8, 0.2, 1] },
                                },
                            }}
                            className="inline-block text-[var(--accent)]"
                        >
                            .
                        </motion.span>
                        <motion.span
                            aria-hidden
                            data-testid="philosophy-typewriter-cursor"
                            variants={{
                                hidden: { opacity: 0 },
                                show: {
                                    opacity: 1,
                                    transition: { delay: 0.95, duration: 0.2 },
                                },
                            }}
                            className="inline-block align-baseline ml-2 md:ml-3"
                        >
                            <motion.span
                                animate={{ opacity: [1, 1, 0, 0] }}
                                transition={{
                                    duration: 1.05,
                                    repeat: Infinity,
                                    ease: 'linear',
                                    times: [0, 0.5, 0.51, 1],
                                }}
                                className="inline-block w-[0.08em] md:w-[0.06em] h-[0.85em] align-baseline translate-y-[0.05em] bg-[var(--accent)] rounded-[1px]"
                            />
                        </motion.span>
                    </motion.blockquote>
                        </div>
                    </div>

                    {/* Bottom row — signature + micro CTA */}
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-10%' }}
                        transition={{ delay: 0.9, duration: 0.7 }}
                        className="relative mt-10 md:mt-14 flex items-center justify-between gap-4 flex-wrap pt-6 border-t border-dashed border-[var(--line)]"
                    >
                        <div className="flex items-center gap-3 font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                            <span className="w-6 h-[1px] bg-[var(--line-2)]" />
                            <span>A rule I live by · signed</span>
                            <span className="font-display-italic normal-case tracking-normal text-[var(--ink-2)]">
                                Himanshu
                            </span>
                        </div>
                        <a
                            href="#work"
                            data-cursor="hover"
                            className="group inline-flex items-center gap-2 text-[var(--ink-2)] hover:text-[var(--accent)] transition-colors font-mono-brand text-[10.5px] uppercase tracking-[0.22em]"
                        >
                            See how I apply it
                            <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">
                                →
                            </span>
                        </a>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Philosophy;
